import Analytics from '../analytics';
import VZThirdParty from '../lib/verizonThirdParty';
import { getLogger } from '../logging/logger';
import LinkHandler from '../linkHandler';

/**
 * @typedef {object} VerizonChannelData
 * @property {string} [channelname] The name for the channel, typically the network
 * @property {string} [channelnumber] The number for the channel to check entitlements for
 */

const logger = getLogger('Verizon');

class VerizonApi extends LinkHandler {
	constructor() {
		super();
		logger.log('[constructor]');

		if (this.serviceIsAvailable()) {
			this.initVZ();
		}
	}

	get linkPattern() {
		return /(?:vz:\/\/\/?)(\w+)\/?([\w\s]+)?\/?([\w\s]+)?\/?([\w\s]+)?/;
	}

	initVZ() {
		logger.log(`[${this.initVZ.name}]`);

		try {
			VZThirdParty.Init();
		} catch (/** @type {any} */ error) {
			logger.error('[VZThirdParty.Init() Failed]', error.toString());
			Analytics.sendEvent('VerizoneThirdParty-Error', {
				error: `[Initialization error] ${error.toString()}`,
			});
		}
	}

	/**
	 * Checks to see if the Verizon API Services is available to be used indicating we
	 * can use Verizon STB functionality.
	 * @returns {boolean} Boolean value indicating if the Verizon API Service is detected.
	 */
	serviceIsAvailable() {
		return Object.prototype.hasOwnProperty.call(window, '__VZThirdParty');
	}

	/**
	 * Parses the given deepLink string in the format of "vz://apiMethodName/param/param/param". It then
	 * breaks the string apart in to the method and parameters and calls the specified method from the
	 * Verizon API.
	 * @param {string} uri String represented deeplink for Verizon integrations
	 * @returns {void} No return value
	 */
	handleLink(uri) {
		logger.log(`[${this.handleLink.name}]`, { uri });

		if (!this.serviceIsAvailable()) {
			const error = 'VZThirdParty Service is not available for deeplink';
			logger.error('[followDeeplink]', error);
			Analytics.sendEvent('VerizonDeepLink-Error', { error });
			return;
		}

		if (!this.linkPattern.test(uri)) return;

		// Extract params and convert number params from string to int
		const [url, apiName, ...allParams] = this.linkPattern.exec(uri) || [];
		const params = allParams
			.filter((param) => ![undefined, null].includes(/** @type {any} */ (param)))
			.map((param) => (/(Channel|Record)/.test(apiName) ? parseInt(param) : param));
		logger.debug('[onLinkClick]', { apiName, params, url });

		const apiMethod = VZThirdParty[apiName];
		if (apiMethod) {
			Analytics.sendEvent('VerizonDeepLink', { url })
				.then(() => apiMethod(...params))
				.catch((error) => {
					logger.error('[onLinkClick]', error.toString());
					Analytics.sendEvent('VerizonDeepLink-Error', {
						url,
						error: error.toString(),
					});
				});
		} else {
			const error = `Invalid API method "${apiName}"`;
			logger.error(error);
			Analytics.sendEvent('VerizonDeepLink-Error', { url, error });
		}
	}

	/**
	 * Requests the entitlment info for the specified channel name for the current Verizon STB User. 
	 * Example response:
		{
			"93": {
				"strChannelName": "GOLF",
				"strLongChNameUncomp": "Golf Channel",
				"intChannelNumber": 93,
				"intHDEquChNumber": 593,
				"intFiosServiceID": 742,
				"bIpStream": "0",
				"b4k": "0",
				"entitlement": true
			},
			"593": {
				"strChannelName": "Golf HD",
				"strLongChNameUncomp": "Golf Channel High Definition",
				"intChannelNumber": 593,
				"intHDEquChNumber": 0,
				"intFiosServiceID": 1920,
				"bIpStream": "0",
				"b4k": "0",
				"entitlement": true
			}
		}
	 * @param {VerizonChannelData} [data] Object containing entity to return entitlement information for.
	 * @returns {Promise<Object>} Promise that resolves an object with keys representing channel entitlment information.
	 */
	checkChannelEntitlement(data) {
		logger.log(`[${this.checkChannelEntitlement.name}]`, { data });

		if (!this.serviceIsAvailable()) {
			const error = new Error(
				`VZThirdParty Service is not available for checkChannelEntitlement: ${JSON.stringify(
					data,
				)}`,
			);
			logger.error(error);
			Analytics.sendEvent('VerizonChannelEntitlement-Error', { error: error.message });

			return Promise.reject(error);
		}

		return new Promise((resolve) => {
			VZThirdParty.GetData('channelentitlement', data, resolve);
		})
			.then((response) => {
				const { responseData } = response;
				Analytics.sendEvent(
					'VerizonChannelEntitlement',
					JSON.stringify({ ...data, ...responseData }),
				);

				if (!responseData || !Object.keys(responseData).length) {
					// Log responses with missing or empty response data for diagnostic purposes. Seems suspicious.
					logger.debug(`[${this.checkChannelEntitlement.name}]`, { response });
					Analytics.sendEvent('VerizonChannelEntitlement', {
						status: 'suspicious response',
						data: JSON.stringify(response),
					});
				}

				return responseData;
			})
			.catch((/** @type {Error} */ error) => {
				logger.error(`[${this.checkChannelEntitlement.name}]`, { error });

				Analytics.sendEvent('VerizonChannelEntitlement-Error', {
					error: error.toString(),
				});

				return Promise.reject(error);
			});
	}

	/**
	 * Convience method. Checks if a user is subscribed for a channel.
	 * @param {string} channelname The channel to check subscription against.
	 * @returns {Promise<any>} Resolves if subscribed, Rejects if not subscribed.
	 */
	isSubscriber(channelname) {
		logger.log(`[${this.isSubscriber.name}]`, { channelname });

		return this.checkChannelEntitlement({ channelname }).then(
			(entitlementData) => {
				logger.log('[checkEntitlementResponse]', { entitlementData });

				const isSubscriber = Object.values(entitlementData)
					.filter(({ strChannelName }) =>
						new RegExp(channelname, 'i').test(strChannelName),
					)
					.some(({ entitlement }) => entitlement);

				Analytics.sendEvent('VerizonSubscriberCheck', {
					status: isSubscriber.toString(),
					data: channelname,
				});

				if (isSubscriber) {
					return Promise.resolve();
				}

				return Promise.reject(
					new Error(`User does not have entitlement for "${channelname}"`),
				);
			},
			(/** @type {Error} */ error) => {
				logger.error('[check entitlment error]', { error });
				return Promise.reject(error);
			},
		);
	}
}

export default new VerizonApi();
